import React, { useState } from 'react';
import RatingCommentModal from '../RatingCommentModal/RatingCommentModal';
import { Button } from '@kaizen/button';
import styles from './RatingActions.scss';

type Props = {
  questionId: string;
  questionText: string;
  defaultCommentContent: string;
  saveUrl: string;
  localeDirection: 'ltr' | 'rtl';
  uiText: {
    addComment: string;
    editComment: string;
    addCommentAria: string;
    editCommentAria: string;
    editCommentAriaDescription: string;
    dialogHeader: string;
    submitLabel: string;
    submittingLabel: string;
    dismissLabel: string;
    repliesInfo: string;
    savingError: string;
    dismissWarningTitle: string;
    dismissWarningBody: string;
    dismissWarningConfirm: string;
    dismissWarningCancel: string;
  };
  showAdministratorCanReplyToCommentMessage: boolean;
};

const RatingCommentButton = (props: Props): React$Element<string> => {
  const {
    uiText,
    questionId,
    questionText,
    defaultCommentContent,
    saveUrl,
    localeDirection,
    showAdministratorCanReplyToCommentMessage,
  } = props;

  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [commentContent, setCommentContent] = useState<string>(
    defaultCommentContent
  );
  const hasComment = commentContent.length > 0 && commentContent.trim() !== '';
  const ariaDescriptionId = hasComment
    ? `aria-description-${questionId}`
    : undefined;
  const [buttonText, setButtonText] = useState<string>(
    hasComment ? uiText.editComment : uiText.addComment
  );
  const [ariaButtonText, setAriaButtonText] = useState<string>(
    hasComment ? uiText.editCommentAria : uiText.addCommentAria
  );

  const handleClick = (): void => {
    setModalOpen(true);
  };

  const handleSave = (updatedComment: string): void => {
    setCommentContent(updatedComment);
    setButtonText(updatedComment ? uiText.editComment : uiText.addComment);
    setAriaButtonText(
      updatedComment ? uiText.editCommentAria : uiText.addCommentAria
    );
  };

  return (
    <>
      {/* This is currently required for Amplitude logging in responses.js */}
      {hasComment && <input name="rating-comment-value" type="hidden" />}
      <Button
        secondary
        label={buttonText}
        aria-label={`${ariaButtonText} "${questionText}"`}
        aria-describedby={ariaDescriptionId}
        onClick={handleClick}
        data-automation-id={`rating-add-comment-${questionId}`}
      />
      {hasComment && (
        <div id={ariaDescriptionId} className={styles.hidden}>
          {uiText.editCommentAriaDescription}: {commentContent}
        </div>
      )}
      <RatingCommentModal
        questionId={questionId}
        questionText={questionText}
        commentContent={commentContent}
        url={saveUrl}
        localeDirection={localeDirection}
        isOpen={modalOpen}
        uiText={uiText}
        dismiss={() => {
          setModalOpen(false);
        }}
        afterSubmit={handleSave}
        showAdministratorCanReplyToCommentMessage={
          showAdministratorCanReplyToCommentMessage
        }
      />
    </>
  );
};

export default RatingCommentButton;
